import {
  DEFAULT_DATE_FORMAT_FNS,
  GroupWithMembers,
  useGetGroupAttendanceAggregateQuery,
  useGetGroupDetailsQuery,
  AggregateSessionScope,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { useParams, useSearchParams } from 'react-router-dom';

import { format } from 'date-fns';
import { useMemo } from 'react';
import { ATTENDANCE_DATE } from '@schooly/constants';
import { canEditAttendanceEntries } from '../../Attendance/permissions';
import { buildTrackingPoints } from '../../Attendance/AttendanceTable/common/trackingPoints';
import { resolveActivePoint, TakeAttendanceTarget } from './take-attendance-utils';
import { AGGREGATE_INCLUDE_ALL_POINTS } from '../../Attendance/AttendanceTable/common/constants';

export const useTakeAttendanceForGroup = (target: TakeAttendanceTarget) => {
  const { id = '' } = useParams<'id'>();

  const { schoolId = '', permissions } = useAuth();
  const [searchParams] = useSearchParams();
  const attendanceDate = searchParams.get(ATTENDANCE_DATE);

  const { showError } = useNotifications();

  const isoDate = attendanceDate || format(new Date(), DEFAULT_DATE_FORMAT_FNS);

  const { data: aggregate, isLoading: isLoadingAggregate } = useGetGroupAttendanceAggregateQuery(
    {
      groupId: id,
      school_id: schoolId,
      date_from: isoDate,
      date_to: isoDate,
      session_scope: AggregateSessionScope.All,
      include: AGGREGATE_INCLUDE_ALL_POINTS,
    },
    {
      enabled: !!id,
      refetchOnMount: 'always',
      onError: showError,
    },
  );

  const { data: groupData, isLoading: isLoadingGroup } = useGetGroupDetailsQuery(
    { id, date: isoDate, with_absence_request: 1 },
    { enabled: !!id, refetchOnMount: 'always' },
  );

  const groupDetails: GroupWithMembers | undefined = groupData?.group;

  const weekDay = aggregate?.total.week_days[0];
  const registerId = weekDay?.register_id ?? null;
  const trackingPoints = useMemo(
    () => (weekDay ? buildTrackingPoints(weekDay.tracking_points) : []),
    [weekDay],
  );

  const activePoint = useMemo(
    () => resolveActivePoint(target, trackingPoints),
    [target.session, target.periodId, trackingPoints, target],
  );

  const canTakeAttendanceBase =
    !!registerId && !!activePoint && canEditAttendanceEntries(permissions);

  return {
    isoDate,
    groupDetails,
    students: groupDetails?.students ?? [],
    aggregate,
    trackingPoints,
    activePoint,
    registerId,
    canTakeAttendanceBase,
    fetching: isLoadingAggregate || isLoadingGroup,
  };
};
